'use client'

import dynamic from 'next/dynamic'
import { useCallback, useMemo, useEffect, useState } from 'react'
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react'
import classNames from 'classnames'
import { getRelativeImageUrl } from '@dustin-web/microsite-components/src/utils/urls'
import {
  useIsNavigating,
  useOptimisticImage,
} from '../../../product-context/product-optimistic-context'
import CarouselSlide from './carousel-slide'
import { CAROUSEL_CONFIG } from './carousel-config'

import './carousel.scss'

const CarouselPagination = dynamic(() => import('./carousel-pagination/carousel-pagination'))
const CarouselPaginationSkeleton = dynamic(
  () => import('./carousel-pagination/carousel-pagination-skeleton')
)
const CarouselNavigationButton = dynamic(
  () => import('./carousel-navigation-button/carousel-navigation-button')
)
const CarouselNavigationSection = dynamic(
  () => import('./carousel-navigation-section/carousel-navigation-section')
)

type Props = {
  imageFilenames: string[]
  isSmallScreen: boolean
  isFullScreen: boolean
  initialIndex: number
  setModalState: ({ isOpen, slideIndex }: { isOpen: boolean; slideIndex: number }) => void
}

const ProductCarousel = ({
  imageFilenames,
  isSmallScreen,
  isFullScreen,
  initialIndex,
  setModalState,
}: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const { optimisticImageId } = useOptimisticImage()
  const isNavigating = useIsNavigating()

  useEffect(() => {
    return () => {
      swiper?.destroy()
    }
  }, [swiper])

  useEffect(() => {
    if (initialIndex !== activeIndex) {
      swiper?.slideTo(initialIndex)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex, swiper])

  const { width, height } = useMemo(
    () => ({
      width: isFullScreen
        ? isSmallScreen
          ? CAROUSEL_CONFIG.slideImageMobileFullscreenWidth
          : CAROUSEL_CONFIG.slideImageFullscreenWidth
        : CAROUSEL_CONFIG.slideImageWidth,
      height: isFullScreen
        ? isSmallScreen
          ? CAROUSEL_CONFIG.slideImageMobileFullscreenHeight
          : CAROUSEL_CONFIG.slideImageFullscreenHeight
        : CAROUSEL_CONFIG.slideImageHeight,
    }),
    [isFullScreen, isSmallScreen]
  )

  const slideImageUrls = useMemo(() => {
    if (imageFilenames.length === 0) {
      return [getRelativeImageUrl(undefined)]
    }

    return imageFilenames.map(filename => getRelativeImageUrl(filename, width, height))
  }, [height, width, imageFilenames])

  const paginationImageUrls = useMemo(() => {
    const imageUrls = imageFilenames.map(filename =>
      getRelativeImageUrl(
        filename,
        CAROUSEL_CONFIG.paginationImageWidth,
        CAROUSEL_CONFIG.paginationImageHeight
      )
    )

    return isSmallScreen || isFullScreen
      ? imageUrls
      : imageUrls.slice(0, CAROUSEL_CONFIG.slidesToShow + 1)
  }, [imageFilenames, isSmallScreen, isFullScreen])

  const hasPagination = paginationImageUrls.length > 1

  const handleSlidePrev = useCallback(() => swiper?.slidePrev(), [swiper])
  const handleSlideNext = useCallback(() => swiper?.slideNext(), [swiper])
  const handleSlideTo = useCallback((index: number) => swiper?.slideTo(index), [swiper])
  const handleOpenModal = useCallback(
    (slideIndex: number) => setModalState({ isOpen: true, slideIndex }),
    [setModalState]
  )

  return (
    <section
      data-testid="carousel-container"
      className={classNames('carousel-container', {
        fullscreen: isFullScreen,
        mobile: isSmallScreen,
        navigating: isNavigating,
      })}>
      <div className="carousel">
        <Swiper
          rewind
          slidesPerView={1}
          initialSlide={initialIndex}
          onSwiper={s => setSwiper(s)}
          onSlideChange={s => setActiveIndex(s.realIndex)}>
          {slideImageUrls.map((imageUrl: string, index: number) => (
            <SwiperSlide key={index}>
              <CarouselSlide
                onClick={() => !isFullScreen && handleOpenModal(index)}
                index={index}
                imageUrl={imageUrl}
                width={width}
                height={height}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {hasPagination && !isSmallScreen && (
          <>
            <CarouselNavigationButton side="previous" onClick={handleSlidePrev} />
            <CarouselNavigationButton side="next" onClick={handleSlideNext} />
          </>
        )}
      </div>

      {hasPagination && isSmallScreen && isFullScreen ? (
        <CarouselNavigationSection
          activeIndex={activeIndex}
          imagesLength={imageFilenames.length}
          onPrevious={handleSlidePrev}
          onNext={handleSlideNext}
        />
      ) : (
        hasPagination && (
          <div data-testid="carousel-pagination" className="carousel-pagination">
            {paginationImageUrls.map((imageUrl, index) => {
              if (optimisticImageId && !isSmallScreen) {
                return <CarouselPaginationSkeleton key={imageUrl} index={index} />
              }

              const showCountOverlay =
                !isFullScreen &&
                index === CAROUSEL_CONFIG.slidesToShow &&
                imageFilenames.length > CAROUSEL_CONFIG.slidesToShow + 1

              const isActive =
                index === activeIndex &&
                (index <= CAROUSEL_CONFIG.slidesToShow || isFullScreen || isSmallScreen)

              return (
                <CarouselPagination
                  index={index}
                  key={imageUrl}
                  imageUrl={imageUrl}
                  isSmallScreen={isSmallScreen}
                  width={CAROUSEL_CONFIG.paginationImageWidth}
                  height={CAROUSEL_CONFIG.paginationImageHeight}
                  isActive={isActive}
                  showCountOverlay={showCountOverlay}
                  overlayCount={imageFilenames.length - (CAROUSEL_CONFIG.slidesToShow + 1)}
                  setShowModal={() => handleOpenModal(index)}
                  onClick={() => handleSlideTo(index)}
                />
              )
            })}
          </div>
        )
      )}
    </section>
  )
}

export default ProductCarousel
